/**
 * SCWT-PM-V2 Phase 3 — Local Partner tab (rich view).
 * Same content as the Overview snapshot but with more room for
 * contact details, region and a "Contact" CTA.
 */
import { useEffect, useState } from "react";
import { Loader2, Mail, Phone, MapPin, ShieldCheck, ExternalLink } from "lucide-react";
import api from "@/lib/api";

export default function PmLocalPartnerTab() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/property-manager/local-partner");
        setSnap(data);
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-700" /></div>;
  }

  const lp = snap?.local_partner;
  if (!lp) {
    return (
      <div data-testid="pm-local-partner-empty" className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
        You don't have a linked PWS Partner yet. Once your local partner
        invites your agency (or an admin links you), their details will
        appear here.
      </div>
    );
  }

  return (
    <div data-testid="pm-local-partner-tab" className="space-y-6">
      <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
          <ShieldCheck className="w-3.5 h-3.5" /> Your local PWS Partner
        </div>
        <h1 data-testid="pm-lp-name" className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900">
          {lp.business_name}
        </h1>
        {lp.region && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600">
            <MapPin className="w-4 h-4" /> {lp.region}
          </div>
        )}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {lp.email && (
            <a href={`mailto:${lp.email}`}
                data-testid="pm-lp-email"
                className="rounded-xl bg-slate-50 border border-slate-200 p-4 hover:border-cyan-300 transition-colors">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
              <div className="text-slate-900 font-semibold text-sm break-all">{lp.email}</div>
            </a>
          )}
          {lp.phone && (
            <a href={`tel:${lp.phone}`}
                data-testid="pm-lp-phone"
                className="rounded-xl bg-slate-50 border border-slate-200 p-4 hover:border-cyan-300 transition-colors">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1">
                <Phone className="w-3.5 h-3.5" /> Phone
              </div>
              <div className="text-slate-900 font-semibold text-sm">{lp.phone}</div>
            </a>
          )}
        </div>
      </section>

      <div className="text-xs text-slate-500 px-2">
        Your local PWS Partner handles any recommended follow-up water
        work with the landlord's consent. Aware Water Testing remains
        the primary platform for tests, reports and reminders.
      </div>
    </div>
  );
}
